import { IconArrowLeft, IconArrowRight, IconBrandAmazon } from "@tabler/icons-react";
import type { ComponentProps } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

export function WizardFooter({
  step,
  canContinueStepOne,
  isSubmitting = false,
  onBack,
  onContinue,
  onDiscard,
}: {
  step: 1 | 2 | 3;
  canContinueStepOne: boolean;
  isSubmitting?: boolean;
  onBack: () => void;
  onContinue: () => void;
  onDiscard: () => void;
}) {
  const { t } = useTranslation("campaignCreation");
  if (step === 1) {
    return (
      <div className="sticky bottom-0 mt-auto -mx-6 flex items-center justify-center border-t border-line bg-canvas/95 px-6 py-4 backdrop-blur">
        <div className="flex gap-3">
          <DiscardCampaignDialog
            trigger={<Button type="button" variant="outline" className="w-56" />}
            onDiscard={onDiscard}
          />
          <Button
            type="button"
            className="w-56"
            disabled={!canContinueStepOne}
            onClick={onContinue}
          >
            {t("actions.next")}
          </Button>
        </div>
      </div>
    );
  }
  if (step === 2) {
    return (
      <div className="mt-4 -mx-1 flex items-center justify-center border-t border-line bg-canvas px-6 py-4">
        <div className="flex gap-3">
          <DiscardCampaignDialog
            trigger={<Button type="button" variant="outline" className="w-56" />}
            onDiscard={onDiscard}
          />
          <Button type="button" className="w-56" onClick={onContinue}>
            {t("actions.save")}
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="sticky bottom-0 mt-5 flex items-center justify-between border-t border-line bg-canvas/95 py-4 backdrop-blur">
      <DiscardCampaignDialog
        trigger={<Button type="button" variant="ghost" disabled={isSubmitting} />}
        onDiscard={onDiscard}
      />
      <div className="flex gap-2">
        {step > 1 ? (
          <Button type="button" variant="outline" disabled={isSubmitting} onClick={onBack}>
            <IconArrowLeft data-icon="inline-start" />
            {t("actions.back")}
          </Button>
        ) : null}
        <Button type="button" loading={isSubmitting} onClick={onContinue}>
          {step === 3 ? t("actions.create") : t("actions.continue")}
          {step < 3 ? (
            <IconArrowRight data-icon="inline-end" />
          ) : (
            <IconBrandAmazon data-icon="inline-end" />
          )}
        </Button>
      </div>
    </div>
  );
}

function DiscardCampaignDialog({
  trigger,
  onDiscard,
}: {
  trigger: ComponentProps<typeof AlertDialogTrigger>["render"];
  onDiscard: () => void;
}) {
  const { t } = useTranslation("campaignCreation");
  return (
    <AlertDialog>
      <AlertDialogTrigger render={trigger}>{t("actions.cancel")}</AlertDialogTrigger>
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("discard.title")}</AlertDialogTitle>
          <AlertDialogDescription>{t("discard.description")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogClose render={<Button variant="outline" />}>
            {t("actions.keepEditing")}
          </AlertDialogClose>
          <AlertDialogClose render={<Button variant="destructive" onClick={onDiscard} />}>
            {t("actions.discard")}
          </AlertDialogClose>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
